import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React, {useEffect, useState, useCallback, useRef} from 'react';

import {PAGE_TYPE_CART} from '@udemy/discovery-api';
import {ClickEvent, Tracker, TrackImpression} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {AlertBanner} from '@udemy/react-messaging-components';
import {isAndroidNative, safelySetInnerHTML} from '@udemy/shared-utils';
import {SHOPPING_LIST_ACTIONS, ShoppingClient, CouponStore, ShoppingItem} from '@udemy/shopping';

import {getConfig} from 'src/features/cart/config';
import {showFooterWhenContentReady} from 'src/features/footer/lib/content-ready';
import {RecommendationsFetchOptions} from 'src/features/recommendations/types/recommendations-fetch-options.interface';
import {getConfig as getShoppingClientConfig} from 'src/features/shopping-client/config';
import {
    EMPTY_CART_FEATURE_VARIANT,
    SHOPPING_CART_VARIANT_MOBILE,
    CART_RECOMMENDATION_FEATURES,
} from 'src/lib/experimentation-helper';

import {useFetchCartMessageNotice} from '../../hooks/use-cart-message-notice';
import styles from './cart.module.less';
import {CartSkeleton} from './components/cart-skeleton/cart-skeleton.react-component';
import {CheckoutPane} from './components/checkout-pane/checkout-pane.react-component';
import {DiscoveryUnits} from './components/discovery-units/discovery-units.react-component';
import {CartMessage} from './components/notices/cart-message.react-component';
import {Notifications} from './components/notifications/notifications.react-component';
import {PopularTopicsUnitComponent} from './components/popular-topics-unit/popular-topics-unit.react-component';
import {Recommendations} from './components/recommendations/recommendations.react-component';
import {ShoppingListTrackActionsProps} from './components/shopping-list/shopping-list.react-component';
import {ShoppingListsPane} from './components/shopping-lists-pane/lists-pane.react-component';
import {CartSliderMenu} from './components/slider-menu/cart-slider-menu.react-component';

interface CartProps {
    showSaveForLaterAction?: boolean;
    showCodingExercisesBadge?: boolean;
}

// Only exported for testing purposes
export const sendShoppingListActionTrackingEvents = ({
    action,
    item,
    destListName,
}: ShoppingListTrackActionsProps) => {
    const clickEventProps = {
        trackingId: item.buyable.frontendTrackingId,
        relatedObjectType: item.buyable.buyable_object_type,
        relatedObjectId: item.buyable.id,
    };

    if (action === SHOPPING_LIST_ACTIONS.REMOVE) {
        Tracker.publishEvent(
            new ClickEvent({
                ...clickEventProps,
                componentName: 'cart_list_remove_action',
            }),
        );
    } else if (action === SHOPPING_LIST_ACTIONS.MOVE) {
        if (destListName === 'cart') {
            Tracker.publishEvent(
                new ClickEvent({
                    ...clickEventProps,
                    componentName: 'cart_list_move_to_cart_action',
                }),
            );
        } else if (destListName === 'wishlist') {
            Tracker.publishEvent(
                new ClickEvent({
                    ...clickEventProps,
                    componentName: 'cart_list_move_to_wishlist_action',
                }),
            );
        } else if (destListName === 'saved_for_later') {
            Tracker.publishEvent(
                new ClickEvent({
                    ...clickEventProps,
                    componentName: 'cart_list_move_to_saved_for_later_action',
                }),
            );
        }
    }
};

export const Cart = observer(
    ({showSaveForLaterAction = true, showCodingExercisesBadge = false}: CartProps) => {
        const i18n = useI18n();
        const {gettext, interpolate, ngettext} = i18n;
        const config = getConfig(gettext, interpolate, ngettext);
        const shoppingClientConfig = getShoppingClientConfig(gettext);
        const isMobile = useMatchMedia('mobile-max');
        const [hasRemovedHiddenFromFooter, setHasRemovedHiddenFromFooter] = useState(false);
        const [shoppingClientStatus, setShoppingClientStatus] = useState('');
        const [recommendationsOptions, setRecommendationsOptions] =
            useState<RecommendationsFetchOptions>({course_ids: ''});
        const [justAddedBuyableItem, setJustAddedBuyableItem] = useState<
            ShoppingItem['buyable'] | undefined
        >();
        const [couponStore, setCouponStore] = useState<CouponStore>();
        const [isCheckoutButtonVisible, setIsCheckoutButtonVisible] = useState(true);
        const checkoutPaneSentinelRef = useRef<HTMLDivElement>(null);

        // Experimentation
        const {featureVariantAssignments} = useFeatures([
            EMPTY_CART_FEATURE_VARIANT,
            SHOPPING_CART_VARIANT_MOBILE,
            CART_RECOMMENDATION_FEATURES,
        ]);
        const shoppingCartVariantMobile =
            featureVariantAssignments.get(SHOPPING_CART_VARIANT_MOBILE)?.configuration?.enabled ??
            false;
        const emptyCartFeatureVariant =
            featureVariantAssignments.get(EMPTY_CART_FEATURE_VARIANT)?.configuration.feature ??
            false;
        const disableRecommedationsFeature =
            featureVariantAssignments.get(CART_RECOMMENDATION_FEATURES)?.configuration
                .disableRecommedations ?? false;

        useEffect(() => {
            if (!shoppingCartVariantMobile) return;
            const sentinel = checkoutPaneSentinelRef.current;
            if (!sentinel) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsCheckoutButtonVisible(entry.isIntersecting);
                },
                {threshold: 0},
            );
            observer.observe(sentinel);
            return () => observer.disconnect();
        }, [shoppingClientStatus, shoppingCartVariantMobile]);

        const enableRecommendationsForWeb = !(disableRecommedationsFeature && isMobile);
        const showAddToCartInCard = !!isMobile;
        const showAddToCardInQuickView = !isMobile;

        const {cartMessage, isCartMessageLoading} = useFetchCartMessageNotice();

        const trackExperimentImpressionEvent = () => {
            const experimentAssignment = featureVariantAssignments.get(EMPTY_CART_FEATURE_VARIANT);
            if (experimentAssignment) {
                Tracker.publishEvent(
                    new ExperimentImpressionEvent(
                        experimentAssignment.experimentIds,
                        experimentAssignment.isLocalDefault,
                    ),
                );
            }
        };

        const setCourseIdsForReco = () => {
            const shoppingCartItems =
                [
                    ShoppingClient.lists.cart,
                    ShoppingClient.lists.saved_for_later,
                    ShoppingClient.lists.wishlist,
                ].find((list) => list.items && list.items.length > 0)?.items ?? [];
            setRecommendationsOptions({
                course_ids: shoppingCartItems
                    .map((item: ShoppingItem) => item.buyable.id)
                    .map(Number)
                    .filter(Boolean)
                    .join(','),
            });
        };

        const trackShoppingListActions = ({
            action,
            item,
            destListName,
        }: ShoppingListTrackActionsProps) => {
            setCourseIdsForReco();
            sendShoppingListActionTrackingEvents({action, item, destListName});
        };

        const setJustAddedBuyable = useCallback(() => {
            const params = new URLSearchParams(window.location.search);
            const buyableIdString = params.get('added_buyable_id');
            const buyableId = buyableIdString ? parseInt(buyableIdString, 10) : null;
            const buyableType = params.get('added_buyable_type');
            if (buyableType && buyableId) {
                const buyableItem = ShoppingClient.lists.cart.findItemByBuyable(
                    buyableType,
                    buyableId,
                );
                setJustAddedBuyableItem(buyableItem?.buyable);
            }
        }, []);

        useEffect(() => {
            if (!couponStore) {
                ShoppingClient.couponStore = new CouponStore(ShoppingClient, i18n);
                setCouponStore(ShoppingClient.couponStore ?? undefined);
                setCourseIdsForReco();
            }
        }, [couponStore, i18n]);

        useEffect(() => {
            if (shoppingClientStatus === shoppingClientConfig.storage.status.ready) {
                setCourseIdsForReco();
            }
        }, [shoppingClientStatus, shoppingClientConfig.storage.status.ready]);

        useEffect(() => {
            if (shoppingClientStatus === shoppingClientConfig.storage.status.ready) {
                setJustAddedBuyable();
            }
        }, [setJustAddedBuyable, shoppingClientConfig.storage.status.ready, shoppingClientStatus]);

        useEffect(() => {
            // Footer is initially hidden to optimize CLS. Show it when content is loaded.
            if (
                !hasRemovedHiddenFromFooter &&
                [
                    shoppingClientConfig.storage.status.unAvailable,
                    shoppingClientConfig.storage.status.ready,
                ].includes(shoppingClientStatus)
            ) {
                setHasRemovedHiddenFromFooter(true);
                showFooterWhenContentReady();
            }
        }, [
            hasRemovedHiddenFromFooter,
            shoppingClientConfig.storage.status.ready,
            shoppingClientConfig.storage.status.unAvailable,
            shoppingClientStatus,
        ]);

        const effectDep = ShoppingClient.status.get() !== shoppingClientStatus;
        useEffect(() => {
            const status = ShoppingClient.status.get();
            setShoppingClientStatus(status);
        }, [effectDep]);

        const renderSections = () => {
            switch (shoppingClientStatus) {
                case shoppingClientConfig.storage.status.unAvailable:
                    return (
                        <div className={styles['shopping-section']}>
                            <span>{config.errors(gettext).cartUnavailable}</span>
                        </div>
                    );
                case shoppingClientConfig.storage.status.ready:
                    const isEmptyCart = ShoppingClient.lists.cart.isEmpty;
                    return (
                        <>
                            <div className={styles['notifications-section']}>
                                {!isCartMessageLoading && cartMessage && (
                                    <CartMessage {...cartMessage.data} />
                                )}
                                <Notifications />
                                {justAddedBuyableItem && (
                                    <AlertBanner
                                        udStyle="success"
                                        showCta={false}
                                        title="Congratulations!"
                                        body={interpolate(
                                            gettext("You've successfully added %(courseTitle)s."),
                                            {
                                                courseTitle: justAddedBuyableItem.title,
                                            },
                                            true,
                                        )}
                                    />
                                )}
                            </div>
                            <TrackImpression trackFunc={trackExperimentImpressionEvent}>
                                <div>
                                    {emptyCartFeatureVariant && isEmptyCart ? (
                                        <>
                                            <div className={styles['shopping-section']}>
                                                <span
                                                    className={styles['empty-cart-message']}
                                                    {...safelySetInnerHTML({
                                                        descriptionOfCaller:
                                                            'cart:empty-cart-message',
                                                        html: config.strings.emptyCartMessage,
                                                    })}
                                                />
                                            </div>
                                            <span className={styles['learners-are-viewing']}>
                                                {config.strings.learnersAreViewing}
                                            </span>
                                            <div className={styles['discovery-units-container']}>
                                                <DiscoveryUnits />
                                            </div>
                                            <PopularTopicsUnitComponent />
                                        </>
                                    ) : (
                                        <div className={styles['shopping-section']}>
                                            <div className={styles['shopping-lists-pane']}>
                                                <ShoppingListsPane
                                                    showSaveForLaterAction={showSaveForLaterAction}
                                                    trackAction={trackShoppingListActions}
                                                    shoppingCartVariantMobile={
                                                        shoppingCartVariantMobile
                                                    }
                                                    couponStore={couponStore}
                                                    showCodingExercisesBadge={
                                                        showCodingExercisesBadge
                                                    }
                                                    isEmptyCart={isEmptyCart}
                                                />
                                            </div>
                                            {!isEmptyCart && (
                                                <div
                                                    className={styles['checkout-pane']}
                                                    ref={checkoutPaneSentinelRef}
                                                >
                                                    <CheckoutPane
                                                        couponStore={couponStore}
                                                        shoppingCartVariantMobile={
                                                            shoppingCartVariantMobile
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </TrackImpression>
                            {enableRecommendationsForWeb &&
                                !isAndroidNative() &&
                                recommendationsOptions.course_ids.length > 0 && (
                                    <div className={styles['recommendations-section']}>
                                        <Provider
                                            showCodingExercisesBadge={showCodingExercisesBadge}
                                        >
                                            <Recommendations
                                                showAddToCartInCard={showAddToCartInCard}
                                                showAddToCartInQuickview={showAddToCardInQuickView}
                                                pageType={PAGE_TYPE_CART}
                                                options={recommendationsOptions}
                                                isMobile={isMobile as boolean}
                                            />
                                        </Provider>
                                    </div>
                                )}
                        </>
                    );
                default:
                    return (
                        <div className={styles['shopping-section']}>
                            <CartSkeleton />
                        </div>
                    );
            }
        };

        return (
            <div className={styles['cart-page-container']}>
                <div
                    className={classNames(styles['title-section'], {
                        [styles['always-visible']]: shoppingCartVariantMobile,
                    })}
                >
                    <h1 className="ud-heading-xxxl">{config.strings.shoppingCart}</h1>
                </div>
                {renderSections()}
                {ShoppingClient.lists.cart.items.length > 0 &&
                    (!shoppingCartVariantMobile || !isCheckoutButtonVisible) && (
                        <CartSliderMenu
                            triggerMwebCartStickyClickEvent={shoppingCartVariantMobile}
                        />
                    )}
            </div>
        );
    },
);
